/**
 * Scenario launcher: campaign order with unlocks, difficulty, seed, saves and sandbox
 * (docs/SCENARIOS.md).
 */

import { useEffect, useState } from 'react';
import { CAMPAIGN, DIFFICULTIES, DIFFICULTY_BLURB } from '../../scenarios';
import { getSite } from '../../sites';
import { useSession } from '../../store/session';
import { useEngine } from '../useEngine';
import { listSaves, loadProgress, type Progress } from '../../store/persistence';
import { money } from '../format';

export function Launcher(): JSX.Element {
  const engine = useEngine();
  const difficulty = useSession((s) => s.difficulty);
  const configure = useSession((s) => s.configure);
  const saves = useSession((s) => s.saves);
  const setSaves = useSession((s) => s.setSaves);
  const [progress, setProgress] = useState<Progress>({ best: {}, unlocked: ['s0-handover'] });
  const [seed, setSeed] = useState(20260830);
  const [tab, setTab] = useState<'campaign' | 'sandbox' | 'saves'>('campaign');
  const [sandboxOpts, setSandboxOpts] = useState({
    siteId: 'kettle-narrows',
    startMonth: 5,
    durationDays: 30,
    headwater: 735,
    wet: 0.5,
  });

  useEffect(() => {
    void loadProgress().then(setProgress);
    void listSaves().then(setSaves);
  }, [setSaves]);

  return (
    <div className="scroll" style={{ height: '100%', padding: 'var(--s6)' }}>
      <div style={{ maxWidth: 1020, margin: '0 auto' }}>
        <div style={{ marginBottom: 'var(--s5)' }}>
          <h1 className="title" style={{ fontSize: 34, marginBottom: 4, marginTop: 0 }}>
            Freeboard
          </h1>
          <div style={{ color: 'var(--ink-dim)', maxWidth: 640 }}>
            You are the shift operator. Hold the reservoir between two failures: too high and you
            lose the margin to the crest, too low and you lose head, the refill — and at Kettle
            Narrows, your spillway, whose sill sits fifteen metres below full pool.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 4, marginBottom: 'var(--s4)' }}>
          {(['campaign', 'sandbox', 'saves'] as const).map((t) => (
            <button key={t} className={tab === t ? 'primary' : undefined} onClick={() => setTab(t)}>
              {t === 'campaign' ? 'Campaign' : t === 'sandbox' ? 'Sandbox' : `Saves (${saves.length})`}
            </button>
          ))}
        </div>

        <div className="panel" style={{ marginBottom: 'var(--s4)' }}>
          <div className="label" style={{ marginBottom: 8 }}>
            Difficulty
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {DIFFICULTIES.map((d) => (
              <button
                key={d}
                className={difficulty === d ? 'primary' : undefined}
                onClick={() => configure({ difficulty: d })}
                style={{ flex: '1 1 220px', textAlign: 'left', padding: 10 }}
              >
                <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{d}</div>
                <div style={{ color: difficulty === d ? 'rgba(255,255,255,0.8)' : 'var(--ink-dim)', fontSize: 12 }}>
                  {DIFFICULTY_BLURB[d]}
                </div>
              </button>
            ))}
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'flex-end' }}>
            <label style={{ maxWidth: 180 }}>
              <div className="label">Seed</div>
              <input
                type="number"
                className="num"
                value={seed}
                onChange={(e) => setSeed(Number(e.target.value) || 1)}
              />
            </label>
            <div style={{ color: 'var(--ink-dim)', fontSize: 12, paddingBottom: 6 }}>
              Same site, scenario, seed and actions reproduce a run exactly.
            </div>
          </div>
        </div>

        {tab === 'campaign' && (
          <div style={{ display: 'grid', gap: 8 }}>
            {CAMPAIGN.map((sc) => {
              const unlocked = progress.unlocked.includes(sc.id) || sc.index === 0;
              const best = progress.best[`${sc.id}:${difficulty}`];
              const site = getSite(sc.siteId);
              return (
                <div
                  key={sc.id}
                  className="panel"
                  style={{ opacity: unlocked ? 1 : 0.45, display: 'grid', gridTemplateColumns: '1fr auto', gap: 16 }}
                >
                  <div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <span className="label">#{sc.index}</span>
                      <h2 className="title" style={{ fontSize: 17, margin: 0 }}>
                        {sc.name}
                      </h2>
                      <span style={{ color: 'var(--ink-dim)' }}>
                        {site.name} · {sc.durationH >= 24 ? `${Math.round(sc.durationH / 24)} d` : `${sc.durationH} h`}
                      </span>
                      {best && (
                        <span style={{ color: best.passed ? 'var(--ok)' : 'var(--warn)', fontSize: 12 }}>
                          {best.passed ? '✓ passed' : '· attempted'} · grade {best.grade} · {money(best.net)}
                        </span>
                      )}
                    </div>
                    <div style={{ color: 'var(--ink-dim)', marginTop: 4 }}>{sc.teaches}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                      className="primary"
                      disabled={!unlocked}
                      data-testid={`start-${sc.id}`}
                      onClick={() => engine.start(sc.siteId, sc.id, difficulty, seed)}
                    >
                      {unlocked ? 'Start shift' : 'Locked'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === 'sandbox' && (
          <div className="panel" style={{ display: 'grid', gap: 12 }}>
            <div style={{ color: 'var(--ink-dim)' }}>
              Free operation with seeded weather from the basin climatology. No objectives, nothing
              unlocks. Run a day to try a gate sequence, or a year to watch a full annual cycle.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
              <label>
                <div className="label">Site</div>
                <select
                  value={sandboxOpts.siteId}
                  onChange={(e) => setSandboxOpts((o) => ({ ...o, siteId: e.target.value }))}
                >
                  <option value="kettle-narrows">Kettle Narrows</option>
                  <option value="lower-sable">Lower Sable</option>
                  <option value="glacier-tarn">Glacier Tarn</option>
                </select>
              </label>
              <label>
                <div className="label">Start month</div>
                <input
                  type="number"
                  min={1}
                  max={12}
                  className="num"
                  value={sandboxOpts.startMonth}
                  onChange={(e) => setSandboxOpts((o) => ({ ...o, startMonth: Number(e.target.value) }))}
                />
              </label>
              <label>
                <div className="label">Days</div>
                <input
                  type="number"
                  min={1}
                  max={365}
                  className="num"
                  value={sandboxOpts.durationDays}
                  onChange={(e) => setSandboxOpts((o) => ({ ...o, durationDays: Number(e.target.value) }))}
                />
              </label>
              <label>
                <div className="label">Start pool (m)</div>
                <input
                  type="number"
                  className="num"
                  value={sandboxOpts.headwater}
                  onChange={(e) => setSandboxOpts((o) => ({ ...o, headwater: Number(e.target.value) }))}
                />
              </label>
              <label>
                <div className="label">Wetness</div>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={sandboxOpts.wet}
                  onChange={(e) => setSandboxOpts((o) => ({ ...o, wet: Number(e.target.value) }))}
                />
              </label>
            </div>
            <div>
              <button
                className="primary"
                onClick={() => engine.start(sandboxOpts.siteId, 'sandbox', difficulty, seed, sandboxOpts)}
              >
                Start sandbox
              </button>
            </div>
          </div>
        )}

        {tab === 'saves' && (
          <div style={{ display: 'grid', gap: 8 }}>
            {saves.length === 0 && (
              <div className="panel" style={{ color: 'var(--ink-dim)' }}>
                No saves yet. Use Save from the top bar during a shift.
              </div>
            )}
            {saves.map((sv) => (
              <div key={sv.name} className="panel" style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{sv.name}</div>
                  <div style={{ color: 'var(--ink-dim)' }} className="num">
                    {sv.scenarioId} · {sv.difficulty} · minute {sv.savedAtMinute.toLocaleString('en-US')}
                  </div>
                </div>
                <button className="primary" onClick={() => engine.loadSave(sv.name)}>
                  Resume
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 'var(--s6)', color: 'var(--ink-dim)', fontSize: 12 }}>
          Kettle Narrows, Lower Sable and Glacier Tarn are fictional composites of Columbia-basin
          facilities. The numbers are internally consistent; no real plant is being represented.
        </div>
      </div>
    </div>
  );
}
